import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getDailySales } from '../../services/api';
import { 
  ShoppingBagIcon, 
  CubeIcon, 
  CurrencyRupeeIcon,
  BuildingStorefrontIcon 
} from '@heroicons/react/24/outline';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await getDailySales();
      setSalesData(response.data.sales);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      title: 'Today\'s Sales',
      value: `₹${salesData?.total_revenue || 0}`,
      icon: CurrencyRupeeIcon,
      color: 'bg-green-500'
    },
    {
      title: 'Total Orders',
      value: salesData?.total_orders || 0,
      icon: ShoppingBagIcon,
      color: 'bg-blue-500'
    },
    {
      title: 'Self Service',
      value: salesData?.self_service_orders || 0,
      icon: BuildingStorefrontIcon,
      color: 'bg-purple-500'
    },
    {
      title: 'Low Stock Items',
      value: '3',
      icon: CubeIcon,
      color: 'bg-red-500'
    }
  ];

  const chartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Sales',
        data: [65, 78, 90, 85, 95, 110, 120],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Weekly Sales Trend'
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Welcome, {admin?.username}</span>
              <button
                onClick={logout}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <Line data={chartData} options={chartOptions} />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/admin/products')}
                className="w-full bg-blue-50 text-blue-600 py-2 px-4 rounded-lg hover:bg-blue-100"
              >
                Manage Products
              </button>
              <button
                onClick={() => navigate('/admin/fridges')}
                className="w-full bg-green-50 text-green-600 py-2 px-4 rounded-lg hover:bg-green-100"
              >
                Manage Fridges
              </button>
              <button
                onClick={() => navigate('/admin/assign-products')}
                className="w-full bg-purple-50 text-purple-600 py-2 px-4 rounded-lg hover:bg-purple-100"
              >
                Assign Products
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Low Stock Alert</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Fridge A-101</span>
                <span className="text-red-500 font-medium">Milk: 2 left</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Fridge B-202</span>
                <span className="text-red-500 font-medium">Eggs: 3 left</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Fridge C-303</span>
                <span className="text-red-500 font-medium">Bread: 1 left</span>
              </div>
              <button className="w-full mt-4 bg-red-50 text-red-600 py-2 px-4 rounded-lg hover:bg-red-100">
                View All
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Today's Refill Schedule</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Fridge A-101</span>
                <span className="text-green-500 text-sm">10:00 AM</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Fridge B-202</span>
                <span className="text-green-500 text-sm">2:00 PM</span>
              </div>
              <button className="w-full mt-4 bg-green-50 text-green-600 py-2 px-4 rounded-lg hover:bg-green-100">
                Manage Schedule
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;